import React, { useCallback, useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import CalendarPage from "../pages/Calender";
import CalendarHr from "../pages/CalenderHr";
import { Button } from "@/components/ui/button";
import { CalendarPlus, PlusCircle } from "lucide-react";
import { API, ym } from "@/App";
import api from "@/api/axios";
import { useAuth } from "@/hooks/AuthContext";
import { toast } from "react-hot-toast";
import GoogleCalendarConnect from "@/components/GoogleCalendarConnect";
import GoogleEventDialog from "@/components/GoogleEventDialog";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {HolidaysAndEventsDialog} from "@/components/HolidaysAndEventsDialog";
import {MonthPicker} from "@/components/month-picker";

function CalendarContainer() {
  const { user } = useAuth(); 
  const [month, setMonth] = useState(ym(new Date())); 
  const [selectedUser, setSelectedUser] = useState(null);
  const [allMembers, setAllMembers] = useState([]);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [form, setForm] = useState({ type: "Casual", start_date: "", end_date: "", reason: "" });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [entryType, setEntryType] = useState("Holiday");
  const [googleRefreshKey, setGoogleRefreshKey] = useState(0);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [isGoogleEventDialogOpen, setIsGoogleEventDialogOpen] = useState(false);

  // Stable identity: passed to GoogleCalendarConnect, which keeps it in a ref.
  const handleGoogleStatus = useCallback((status) => {
    setGoogleConnected(!!status?.connected);
  }, []);

  const handleOpenDialog = (type) => {
    setEntryType(type);
    setIsDialogOpen(true);
  };

  const isManager = user?.role === "ADMIN" || user?.role === "PM";

  const refreshMembers = async () => {
    try {
      const allMember = await api.get(`${API}/users`).then((x) => x.data).catch(() => []);
      setAllMembers(allMember);
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    if (!user?.id || !form.start_date) return;

    try {
      await api.post(`${API}/calendar/leave-requests`, {
        user_id: user.id,
        ...form,
        end_date: form.end_date || form.start_date
      });
      setForm({ type: "Casual", start_date: "", end_date: "", reason: "" });
      setIsLeaveDialogOpen(false);
      // Optionally refresh calendar data
    } catch (ex) {
      alert(ex.response?.data?.detail || "Failed to submit leave request");
    }
  };

  useEffect(() => {
    refreshMembers();
  }, [month, user?.id, selectedUser]);

  // Handle the redirect back from the Google OAuth consent screen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleResult = params.get("google");
    if (!googleResult) return;

    if (googleResult === "connected") {
      toast.success("Google Calendar connected");
      setGoogleRefreshKey((k) => k + 1);
    } else if (googleResult === "denied") {
      toast.error("Google Calendar access was denied");
      setGoogleRefreshKey((k) => k + 1);
    } else if (googleResult === "error") {
      toast.error("Couldn't connect Google Calendar. Please try again.");
      setGoogleRefreshKey((k) => k + 1);
    }

    // Strip the param so a refresh doesn't re-toast.
    params.delete("google");
    const query = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
    );
  }, []);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Calendar & Leave</h1>
          <p>Your monthly overview of events, attendance, and leave requests.</p>
        </div>

        {/* Filters */}
        <div className="row gap-5">
          {isManager && (
            <div className="flex flex-col">
              <select
                className="border rounded-md px-3 py-2"
                value={selectedUser?.id || ""}
                onChange={(e) => {
                  const userSelected = allMembers.find(u => u.id === e.target.value);
                  setSelectedUser(userSelected || null);
                }}
              >
                <option value="">Select User</option>
                {allMembers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col gap-5">
            <div className="controls">
              {/*<Label>Month</Label>*/}
              <MonthPicker value={month} onChange={setMonth} />
            </div>

          </div>
        </div>

      </div>
      <div className=" flex flex-row gap-5 flex-end">
        <Button onClick={() => setIsLeaveDialogOpen(true)}>
          <PlusCircle className="mr-2 h-4 w-4" /> Request Leave
        </Button>
        {isManager && <Button onClick={() => handleOpenDialog("Holiday")}>
          <PlusCircle className="mr-2 h-4 w-4" /> Add Holiday
        </Button> }
        {isManager && <Button onClick={() => handleOpenDialog("Event")}>
          <PlusCircle className="mr-2 h-4 w-4" /> Add Event
        </Button> }

        {/* Only offered once an account is linked — there is nowhere to create
            the event otherwise, and the button would just error. */}
        {googleConnected && (
          <Button variant="outline" onClick={() => setIsGoogleEventDialogOpen(true)}>
            <CalendarPlus className="mr-2 h-4 w-4" /> New Meeting
          </Button>
        )}

        <div className="ml-auto flex items-center">
          <GoogleCalendarConnect
            onChange={() => setGoogleRefreshKey((k) => k + 1)}
            onStatusChange={handleGoogleStatus}
          />
        </div>
      </div>

      {/* Calendar */}
      {isManager && selectedUser ? (
        <CalendarHr month={month} setMonth={setMonth} userId={selectedUser.id} />
      ) : (
        <CalendarPage month={month} setMonth={setMonth} googleRefreshKey={googleRefreshKey} />
      )}

      <HolidaysAndEventsDialog
          isDialogOpen={isDialogOpen}
          setIsDialogOpen={setIsDialogOpen}
          entryType={entryType}
      />

      <GoogleEventDialog
        open={isGoogleEventDialogOpen}
        onOpenChange={setIsGoogleEventDialogOpen}
        // Bumping the key re-fetches Google events so the new one appears
        // without a page reload.
        onCreated={() => setGoogleRefreshKey((k) => k + 1)}
      />

      {/* Leave Request Dialog */}
      <Dialog open={isLeaveDialogOpen} onOpenChange={setIsLeaveDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
            <DialogDescription>
              Submit your leave request. It will be sent to your manager for approval.
            </DialogDescription>
          </DialogHeader>

          <form id="leave-form" onSubmit={submitLeave} className="grid gap-4 py-4 sm:grid-cols-2">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Casual">Casual</SelectItem>
                  <SelectItem value="Sick">Sick</SelectItem>
                  <SelectItem value="Earned">Earned</SelectItem>
                  <SelectItem value="Unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Start Date</Label>
              <Input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>

            <div>
              <Label>End Date (optional)</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>

            <div className="sm:col-span-2">
              <Label>Reason</Label>
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
          </form>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsLeaveDialogOpen(false)}>Cancel</Button>
            <Button type="submit" form="leave-form">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CalendarContainer;
